import { useState } from "react";
import { motion } from "framer-motion";
import { Check, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";
import { useWorld } from "../state/World";

/**
 * Email at the door is identity. A CONFIRMED email is what money requires.
 *
 * DELIVERY IS MOCKED — no email provider is wired, so the code is shown here
 * rather than sent. The gate itself is real and enforced server-side on both
 * checkout and confirm.
 */
export const EmailVerify = ({ bright }) => {
  const { me, refresh } = useWorld();
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (!me || me.user.email_verified) return null;

  const ask = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/verify/request", {});
      setSent(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not issue a code.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post("/auth/verify/confirm", { code: code.trim() });
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || "That code does not match.");
    } finally {
      setBusy(false);
    }
  };

  const muted = bright ? "text-[#0f2530]/55" : "text-white/40";
  const ghost = bright
    ? "border-[#0f2530]/25 text-[#0f2530]/75 hover:border-[#9a5a24] hover:text-[#9a5a24]"
    : "border-white/12 text-white/55 hover:border-[var(--stage-gold)]/50 hover:text-white/85";
  const field = bright
    ? "border-[#0f2530]/25 bg-white/45 text-[#0f2530] placeholder:text-[#0f2530]/30 focus:border-[#9a5a24]"
    : "border-white/12 bg-black/25 text-white/85 placeholder:text-white/20 focus:border-[var(--stage-gold)]/50";
  const accent = bright ? "text-[#9a5a24]" : "text-[var(--stage-gold)]";

  return (
    <motion.div
      data-testid="email-verify"
      className="mt-5 border-t pt-4"
      style={{ borderColor: bright ? "rgba(15,37,48,0.16)" : "rgba(255,255,255,0.1)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className={`tracked mb-2 flex items-center gap-1.5 text-[9px] ${muted}`}>
        <ShieldAlert size={11} strokeWidth={1.8} className="text-[var(--stage-ember)]" />
        Confirm email to buy Tokens
      </div>

      {!sent ? (
        <button
          type="button"
          data-testid="verify-request"
          disabled={busy}
          onClick={ask}
          className={`tracked w-full rounded-full border px-3 py-2.5 text-[9px] transition-colors disabled:opacity-40 ${ghost}`}
        >
          {busy ? "Issuing…" : "Send me a code"}
        </button>
      ) : (
        <>
          <p
            data-testid="verify-mock-code"
            className={`mono mb-2.5 text-[10px] leading-relaxed ${muted}`}
          >
            Email delivery is MOCKED. Your code is{" "}
            <span className={accent}>{sent.code}</span>
          </p>
          <div className="flex gap-2">
            <input
              data-testid="verify-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className={`mono min-w-0 flex-1 rounded-full border px-3 py-2 text-[12px] outline-none ${field}`}
            />
            <button
              type="button"
              data-testid="verify-confirm"
              disabled={busy || code.trim().length < 6}
              onClick={confirm}
              className={`grid h-9 w-9 shrink-0 place-items-center rounded-full text-[#1a1206] transition-opacity disabled:opacity-25 ${bright ? "bg-[#9a5a24] text-white" : "bg-[var(--stage-gold)]"}`}
            >
              <Check size={14} strokeWidth={2.4} />
            </button>
          </div>
        </>
      )}

      {err && (
        <p data-testid="verify-error" className="mono mt-2 text-[10px] text-[var(--stage-ember)]">
          {err}
        </p>
      )}
    </motion.div>
  );
};

export default EmailVerify;
